import type { CreativeBriefResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DownloadButton,
  DownloadButtonWrapper,
  DownloadMenu,
  DownloadMenuItem
} from '../styles/briefPanel.styles';
import useCreativeBriefsApi from '../useCreativeBriefsApi';

type DownloadDropdownProps = {
  briefId: string;
  assets: CreativeBriefResponse['assets'];
};

const openDownloadUrl = (url: string) => {
  try {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to open asset download URL:', error);
    window.open(url, '_blank');
  }
};

const DownloadDropdown = ({ briefId, assets }: DownloadDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { getAssetDownloadUrl } = useCreativeBriefsApi();

  const asset = assets[0];
  const hasOverlay = asset ? asset.overlayUrl && asset.overlayUrl !== asset.url : false;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleDownload = useCallback(
    async (variant: 'original' | 'overlay') => {
      if (!asset) return;

      const result = await getAssetDownloadUrl({
        briefId,
        assetId: asset.assetId,
        overlay: variant === 'overlay'
      });

      if (result.error) {
        // eslint-disable-next-line no-console
        console.error('Failed to get download URL, falling back to preview URL:', result.error);
        openDownloadUrl(variant === 'overlay' ? asset.overlayUrl : asset.url);
        setIsOpen(false);
        return;
      }

      if (!result.data) {
        setIsOpen(false);
        return;
      }

      openDownloadUrl(result.data.downloadUrl);
      setIsOpen(false);
    },
    [asset, briefId, getAssetDownloadUrl]
  );

  const triggerDownload = useCallback(
    (variant: 'original' | 'overlay') => {
      handleDownload(variant).catch(error => {
        // eslint-disable-next-line no-console
        console.error('Unexpected error while downloading asset:', error);
      });
    },
    [handleDownload]
  );

  if (!asset) return null;

  return (
    <DownloadButtonWrapper ref={menuRef}>
      <DownloadButton
        onClick={e => {
          e.stopPropagation();
          if (hasOverlay) {
            setIsOpen(prev => !prev);
          } else {
            triggerDownload('original');
          }
        }}
        aria-label="Download asset"
      >
        &#8615; Download
      </DownloadButton>
      {!!(isOpen && hasOverlay) && (
        <DownloadMenu>
          <DownloadMenuItem onClick={() => triggerDownload('original')}>
            Without text
          </DownloadMenuItem>
          <DownloadMenuItem onClick={() => triggerDownload('overlay')}>With text</DownloadMenuItem>
        </DownloadMenu>
      )}
    </DownloadButtonWrapper>
  );
};

export default DownloadDropdown;
